import { useEffect, useContext, useState } from 'react';
import Environment from '../../Environment';
import AdditionalCompanyInformation from '../Section/AdditionalCompanyInformation/AdditionalCompanyInformation';
import AppContext from '../../AppContext';
import Section from '../Section/Section';
import { SectionOrderData } from '../model/SectionOrderData';
import { ReportLayout } from '../model/ReportLayout';
import { cachedFetch } from '../../utils/cachedFetch';
export default function ReportContent() {
  const {
    cocode,
    year,
    periodType,
    quarterlyViewMode,
    setStatementType,
    viewMode,
    yearsMode,
    beginRefresh,
    endRefresh
  } = useContext(AppContext);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [sectionOrderResults, setSectionOrderResults] = useState<SectionOrderData[]>();

  // Structural fingerprint — excludes yearsMode. When any of these change, the
  // report identity is different (different company, year, period, view mode),
  // so we tear down the current sections and show the loading placeholder.
  // yearsMode changes take the soft-refresh path: existing sections stay
  // mounted and refetch in the background while the global refresh counter
  // drives the inline spinner in ReportOptions.
  const structuralKey = `${cocode}|${year}|${periodType}|${quarterlyViewMode}|${viewMode}`;
  const [renderedStructuralKey, setRenderedStructuralKey] = useState(structuralKey);
  if (renderedStructuralKey !== structuralKey) {
    setRenderedStructuralKey(structuralKey);
    setIsLoading(true);
    setSectionOrderResults(undefined);
    setError(null);
  }

  useEffect(() => {
    let cancelled = false;
    let ended = false;
    const end = () => {
      if (!ended) {
        ended = true;
        endRefresh?.();
      }
    };

    beginRefresh?.();

    async function load() {
      try {
        let url = `${Environment.API_URL()}/report-layout?cocode=${cocode}&year=${year}`;
        if (periodType) url += `&periodType=${periodType}`;
        if (quarterlyViewMode) url += `&viewMode=${quarterlyViewMode}`;
        if (yearsMode) url += `&yearsMode=${yearsMode}`;
        const res = await cachedFetch(url, {
          headers: {
            Authorization: `${sessionStorage.getItem('bearerToken')}`
          }
        });
        if (cancelled) return;
        if (!res.ok) {
          setError(new Error(res.statusText));
          return;
        }
        const data: ReportLayout = await res.json();
        if (cancelled) return;
        setStatementType(data.statementType);
        setSectionOrderResults(data.sectionOrderResults);
      } catch (e) {
        if (!cancelled) console.log(e);
      } finally {
        if (!cancelled) setIsLoading(false);
        end();
      }
    }
    load();

    return () => {
      cancelled = true;
      end();
    };
  }, [
    cocode,
    year,
    periodType,
    quarterlyViewMode,
    setStatementType,
    viewMode,
    yearsMode,
    beginRefresh,
    endRefresh
  ]);

  // Only show the top-level loading placeholder for the true initial load
  // (before we've ever received a layout). Once we have layout, yearsMode
  // refreshes reuse the mounted sections and show the inline spinner instead.
  if (isLoading && !sectionOrderResults) {
    return <p data-testid="loading-text">Loading report content...</p>;
  } else if (error) {
    return <p>There was an error loading the report: {error.message}</p>;
  } else {
    return (
      <div>
        <AdditionalCompanyInformation />
        {sectionOrderResults &&
          sectionOrderResults?.map((sectionOrderData: SectionOrderData) => {
            return (
              <Section
                sectionKey={sectionOrderData.sectionKey}
                sectionTitle={sectionOrderData.sectionTitle}
                key={sectionOrderData.sectionKey}
              />
            );
          })}
      </div>
    );
  }
}
